import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Chip,
  Pagination,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';

const PayrollTable = ({
  payrolls,
  isLoading,
  page,
  totalPages,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onDownloadSlip,
}) => {
  const formatCurrency = (amount, currency = 'PKR') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount || 0);

  const getStatusColor = (status) => {
    const normalizedStatus = String(status || '').toUpperCase();
    const colors = {
      PENDING: 'warning',
      APPROVED: 'info',
      PAID: 'success',
      PROCESSED: 'success',
      'ON HOLD': 'default',
      REJECTED: 'error',
      CANCELLED: 'error',
    };
    return colors[normalizedStatus] || 'default';
  };

  return (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payroll Records
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Basic Salary</TableCell>
                  <TableCell align="right">Gross Pay</TableCell>
                  <TableCell align="right">Net Pay</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  payrolls.map((payroll) => (
                    <TableRow key={payroll.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {payroll.employee?.full_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {payroll.employee?.employee_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {payroll.month} {payroll.year}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(payroll.basic_salary, payroll.payment_currency)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(payroll.gross_payable, payroll.payment_currency)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(payroll.net_payable, payroll.payment_currency)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payroll.payment_status}
                          color={getStatusColor(payroll.payment_status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {payroll.payment_date ? dayjs(payroll.payment_date).format('MMM DD, YYYY') : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Open Employee Dashboard">
                          <span>
                            <IconButton
                              size="small"
                              component={RouterLink}
                              to={`/hr/employee-dashboard?employee_id=${payroll.employee?.id}`}
                              title="Open Employee Dashboard"
                              disabled={!payroll.employee?.id}
                              sx={{ color: 'info.main' }}
                            >
                              <DashboardIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton size="small" onClick={() => onDownloadSlip?.(payroll)} title="Download Salary Slip">
                          <DownloadIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => onView(payroll)}>
                          <ViewIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => onEdit(payroll)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(payroll)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {!isLoading && payrolls.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                      <Paper
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="body2">No records found.</Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => onPageChange(value)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default PayrollTable;
